import React, { useCallback, useContext, useState, useEffect } from 'react';
import { CancelToken } from 'axios';
import { usePromise } from '../../../Common/hooks/usePromise';
import {
    AgrmType,
    checkAgrmRestartPossible,
    isValidCloudOrEnvironment,
    restartAgrmProcess,
} from '../../api/AGRMApi';
import {
    Icon,
    ITooltipHostStyles,
    Modal,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TooltipHost,
} from '@fluentui/react';
import { useId } from '@uifabric/react-hooks';
import { IconActionButton } from '../../../Common/components/IconActionButton';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { emitUserActionMetric } from '../../../Common/util/metricsUtil';
import {
    UserAction,
    ActionResult,
} from '../../../Common/Enums/Metrics/MetricEnums';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';
import { DeploymentType } from '../../models/DeploymentType';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';

interface IRestartAgrmProcessButtonProps {
    uniqueIdentifier: string;
    type: AgrmType;
    completionIndicator: string;
    serviceTreeId: string;
    highSideReset?: boolean;
    deploymentType?: DeploymentType;
    onRestartSuccess?: () => void;
    monitorId?: string;
}

export const RestartAgrmProcessButton: React.FC<IRestartAgrmProcessButtonProps> =
    (props: IRestartAgrmProcessButtonProps) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);
        const capabilities = useContext(CapabilityContext);

        const [restartTooltipMessage, setRestartTooltipMessage] = useState<
            string | undefined
        >('');
        const [restartAllowed, setRestartAllowed] = useState<boolean>(false);
        const [toolTipTextColor, setToolTipTextColor] = useState<string>(
            theme.semanticColors.errorText
        );
        const [restartSuccessful, setRestartSuccessful] =
            useState<boolean>(false);
        const [restartFailed, setRestartFailed] = useState<boolean>(false);
        const [isProcessing, setIsProcessing] = useState<boolean>(false);

        const restartTerm = props.type === 'Build' ? 'revalidate' : 'restart';
        const propsTypeToLower =
            props.type[0].toLowerCase() +
            props.type.slice(1, props.type.length);
        const typeToLower =
            props.type === 'Build'
                ? propsTypeToLower + '?'
                : props.monitorId
                ? propsTypeToLower +
                  '? \n\nIt is recommended to restart from ADO in public since you have a SRM Monitoring task on this stage.'
                : propsTypeToLower + '?';

        const [result, error, isLoaded] = usePromise(
            useCallback(
                async (cancelToken?: CancelToken) => {
                    if (props.uniqueIdentifier) {
                        return await checkAgrmRestartPossible(
                            props.uniqueIdentifier,
                            props.type,
                            props.serviceTreeId,
                            cancelToken
                        );
                    } else {
                        throw new Error(
                            'Restart check failed. The deployment is missing a Unique Identifier.'
                        );
                    }
                },
                [props.uniqueIdentifier, props.type, props.serviceTreeId]
            ),
            true
        );

        // add the check for the deployment's completionIndicator
        // it's possible the SRE hasn't yet started to process the new request
        // and thus might return true on the restart check
        // SRC will have started a new replication immediately though and set its status to Processing
        const canRestart =
            result?.succeeded === true &&
            result?.data === true &&
            props.completionIndicator !== 'Processing';

        useEffect(() => {
            // Once a restart has been kicked off, the button owns the tooltip (success or
            // failure message). Restarting flips the deployment to 'Processing', which makes
            // canRestart false and would otherwise stomp the success message with a red
            // "cannot be restarted" style message.
            if (restartSuccessful || restartFailed) {
                return;
            }

            if (!isLoaded) {
                setToolTipTextColor(theme.palette.neutralDark);
                setRestartTooltipMessage('Checking restart access...');
            } else if (error) {
                setToolTipTextColor(theme.palette.red);
                setRestartTooltipMessage(
                    result?.message ||
                        'Unable to restart this ' +
                            propsTypeToLower +
                            ' at this time. There was an error checking the status of the deployment or build validation.'
                );
            } else if (!canRestart) {
                setToolTipTextColor(theme.palette.red);
                // result.message describes the Service Tree admin check. Only surface it when
                // that check denied access - the "user is a Service Tree admin" variant tells
                // the user nothing useful here and reads like an error in red text.
                const accessDeniedMessage =
                    result?.data === true ? undefined : result?.message;
                setRestartTooltipMessage(
                    accessDeniedMessage ||
                        'The ' +
                            propsTypeToLower +
                            ' cannot be restarted. It is either currently in progress, or one of its dependencies is in progress or has failed.'
                );
            } else if (canRestart) {
                setToolTipTextColor(theme.palette.neutralDark);
                if (props.type === AgrmType.Build) {
                    setRestartTooltipMessage('Click to revalidate build.');
                } else {
                    setRestartTooltipMessage(
                        result?.message
                            ? result.message + '\nClick to redeploy.'
                            : 'Click to redeploy.'
                    );
                }
            }
        }, [
            canRestart,
            error,
            isLoaded,
            result,
            propsTypeToLower,
            props.type,
            typeToLower,
            restartSuccessful,
            restartFailed,
            theme.palette.red,
            theme.palette.neutralDark,
        ]);

        useEffect(() => {
            if (canRestart && !restartSuccessful) {
                setRestartAllowed(canRestart);
            } else {
                setRestartAllowed(false);
            }
        }, [canRestart, error, restartSuccessful]);

        // builds dont have reset feedback so set message to empty string if its build type
        const restartMessage =
            props.type === AgrmType.Build
                ? 'After clicking the "Revalidate Build" button, please wait a few minutes for the process to complete. To check the status of the revalidation, you can retrieve the General Build Data.'
                : props.highSideReset
                ? 'This deployment has been restarted one or more times, and confirmation has been received lowside that AGRM is processing or has processed one or more of the restarts.'
                : 'This deployment has not yet been restarted. If you recently restarted this deployment, this status will update once we receive confirmation that AGRM successfully initiated the restart process.';
        const tooltipId = useId('restart-tooltip');
        const hostStyles: Partial<ITooltipHostStyles> = {
            root: {
                display: 'flex',
            },
        };
        const infoButtonStyles = {
            root: {
                paddingLeft: '5px',
                border: restartFailed
                    ? `1px solid ${theme.palette.red}`
                    : undefined,
                borderRadius: restartFailed ? '50%' : undefined,
                color: restartFailed ? theme.palette.red : undefined,
            },
        };

        // Setup Restart Agrm Process
        const publishPromise = useCallback(
            async (cancelToken?: CancelToken) => {
                setRestartAllowed(false);
                setRestartFailed(false);

                var restartResult = ActionResult.FAILED;

                if (props.type !== AgrmType.Build) {
                    setIsProcessing(true);
                }

                try {
                    if (props.uniqueIdentifier) {
                        const result = await restartAgrmProcess(
                            props.uniqueIdentifier,
                            props.type,
                            props.serviceTreeId,
                            cancelToken
                        );

                        if (result.succeeded) {
                            setRestartTooltipMessage(
                                'Successfully restarted ' +
                                    props.type.toLowerCase() +
                                    ' with id ' +
                                    props.uniqueIdentifier
                            );
                            setRestartSuccessful(true);
                            setToolTipTextColor(theme.palette.blue);
                            restartResult = ActionResult.SUCCESS;

                            // Trigger parent to refresh release data
                            props.onRestartSuccess?.();

                            return result;
                        } else {
                            const failureMessage =
                                'Failed to restart. Please refer to the logs for more information.';
                            setRestartTooltipMessage(failureMessage);
                            setToolTipTextColor(theme.palette.red);
                            setRestartSuccessful(false);
                            setRestartFailed(true);

                            // Throw so IconActionButton enters ButtonState.Failed (red Info icon, disabled)
                            throw new Error(result.message ?? failureMessage);
                        }
                    } else {
                        const failureMessage =
                            'Failed to restart. Please refer to the logs for more information.';
                        setRestartSuccessful(false);
                        setRestartFailed(true);
                        setRestartTooltipMessage(failureMessage);
                        setToolTipTextColor(theme.palette.red);
                        throw new Error(failureMessage);
                    }
                } finally {
                    emitUserActionMetric(
                        UserAction.RESTART_SRM_PROCESS,
                        props.type,
                        restartResult,
                        process.env.REACT_APP_BUILD_VERSION
                    );
                    setIsProcessing(false);
                }
            },
            [
                props.type,
                props.uniqueIdentifier,
                theme.palette.blue,
                theme.palette.red,
            ]
        );

        const shouldShowRestartButton = (() => {
            // show the restart button if the cloud is valid.
            // don't worry about access control here, just show the button.
            // it will default to disabled, and only be enabled if the call to the SRE endpoint returns success.
            // we want to show it but leave it disabled, so that even if a user doesn't have access,
            // they'll see it with a message letting them know which group Service Tree has listed as having admin access, so they can request access if applicable.
            const cloud = getConfig(GlobalConfigProperties.Cloud);
            const environment = getConfig(GlobalConfigProperties.Environment);

            if (!isValidCloudOrEnvironment(cloud, environment)) {
                return false;
            }

            // Deployment restarts are limited to M365 pipelines. Everyone else
            // only sees the button in enabled clouds or with the SRM Admin role.
            if (props.type === AgrmType.Deployment) {
                return (
                    props.deploymentType === 'M365' ||
                    props.deploymentType === 'M365RegionAgnostic' ||
                    capabilities.check(Capability.srmAdminRole) ||
                    capabilities.check(Capability.ussec) ||
                    capabilities.check(Capability.usnat) ||
                    capabilities.check(Capability.sovmock)
                );
            }

            return true;
        })();

        return shouldShowRestartButton ? (
            <div className="RestartAgrmProcessButton-root">
                <Modal isOpen={isProcessing} isBlocking={true}>
                    <Stack
                        horizontalAlign="center"
                        verticalAlign="center"
                        style={{ padding: '48px 72px', minWidth: '240px' }}
                        tokens={{ childrenGap: 16 }}
                    >
                        <Spinner size={SpinnerSize.large} />
                        <Text variant="mediumPlus">
                            Processing redeployment request...
                        </Text>
                    </Stack>
                </Modal>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            border: restartFailed
                                ? `1px solid ${theme.palette.red}`
                                : undefined,
                            borderRadius: restartFailed ? '2px' : undefined,
                        }}
                    >
                        <IconActionButton
                            setDisabled={!isLoaded || !restartAllowed}
                            setDisabledOnSuccess={true}
                            setDisabledOnFailure={true}
                            toolTipText={restartTooltipMessage}
                            toolTipTextColor={toolTipTextColor}
                            onClick={publishPromise}
                            initialButtonText={'Restart ' + props.type}
                            initialButtonIcon={'ReceiptCheck'}
                            showConfirmationPopup={true}
                            iconName={'RepeatAll'}
                            failedIconName={'Info'}
                            modalText={
                                'Are you sure you want to ' +
                                restartTerm +
                                ' this ' +
                                typeToLower
                            }
                            differentiator={props.uniqueIdentifier}
                        />
                    </div>
                </div>
            </div>
        ) : null;
    };
